import { api } from "./client";
import type {
  CrewResponse,
  AdminPublic,
  ConsentText,
  DashboardSummary,
  EmployeeDetail,
  EmployeePublic,
  EmployeeUpdate,
  GenericMessage,
  InvitationAdmin,
  InvitationMutationResponse,
  GeocodeCandidate,
  PublicConfig,
  VenueAdmin,
  VenueInput,
  BulkCreateResponse,
  EmployeeOption,
  HistoryEntry,
  MyAssignment,
  ShiftAdmin,
  ShiftCreate,
  ShiftInput,
  ShiftQuery,
  CompliancePanel,
  OutboxItem,
  PhoneConflict,
  RegistrationResult,
  SegmentInfo,
  SmsEvent,
  TemplateInfo,
  VerificationAttempt,
  VerifySent,
} from "./types";

const qs = (params: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && p.set(k, v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const configApi = {
  publicConfig: () => api.get<PublicConfig>("/config/public"),
};

export const adminApi = {
  me: () => api.get<AdminPublic>("/admin/me"),
  dashboard: () => api.get<DashboardSummary>("/admin/dashboard/summary"),
  employees: (q?: string, status?: string) => api.get<EmployeePublic[]>(`/admin/employees${qs({ q, status })}`),
  employee: (id: string) => api.get<EmployeeDetail>(`/admin/employees/${id}`),
  updateEmployee: (id: string, body: EmployeeUpdate) => api.patch<EmployeeDetail>(`/admin/employees/${id}`, body),
  deactivate: (id: string, reason: string) => api.post<EmployeeDetail>(`/admin/employees/${id}/deactivate`, { reason }),
  reactivate: (id: string) => api.post<EmployeeDetail>(`/admin/employees/${id}/reactivate`),
  invitations: (status?: string) =>
    status === "EXPIRING_48H"
      ? api.get<InvitationAdmin[]>(`/admin/invitations${qs({ expiring_within_hours: "48" })}`)
      : api.get<InvitationAdmin[]>(`/admin/invitations${qs({ status })}`),
  createInvitation: (full_name: string, email: string) =>
    api.post<InvitationMutationResponse>("/admin/invitations", { full_name, email }),
  resendInvitation: (id: string) => api.post<InvitationMutationResponse>(`/admin/invitations/${id}/resend`),
  revokeInvitation: (id: string) => api.post<InvitationAdmin>(`/admin/invitations/${id}/revoke`),
  approve: (id: string, use_name: "INVITED" | "SUBMITTED") =>
    api.post<EmployeeDetail>(`/admin/invitations/${id}/approve`, { use_name }),
  reject: (id: string, reason: string) => api.post<InvitationAdmin>(`/admin/invitations/${id}/reject`, { reason }),
};

export const venueApi = {
  list: (q?: string, status?: string) => api.get<VenueAdmin[]>(`/admin/venues${qs({ q, status })}`),
  get: (id: string) => api.get<VenueAdmin>(`/admin/venues/${id}`),
  create: (body: VenueInput) => api.post<VenueAdmin>("/admin/venues", body),
  update: (id: string, body: VenueInput) => api.put<VenueAdmin>(`/admin/venues/${id}`, body),
  archive: (id: string) => api.post<VenueAdmin>(`/admin/venues/${id}/archive`),
  restore: (id: string) => api.post<VenueAdmin>(`/admin/venues/${id}/restore`),
  geocode: (q: string) => api.get<{ candidates: GeocodeCandidate[] }>(`/admin/geocode${qs({ q })}`),
  timezone: (lat: number, lng: number) => api.get<{ timezone: string | null }>(`/admin/timezone?lat=${lat}&lng=${lng}`),
  timezones: () => api.get<string[]>("/admin/timezones"),
};

export const publicApi = {
  validateToken: (token: string) => api.get<{ valid: boolean }>(`/public/invitations/validate${qs({ token })}`),
  consentText: () => api.get<ConsentText>("/public/consent-text"),
  register: (body: { token: string; full_name: string; email: string; phone: string; consent_acknowledged: boolean }) =>
    api.post<RegistrationResult>("/public/register", body),
  verifyResend: (verification_token: string) => api.post<VerifySent>("/public/phone-verification/resend", { verification_token }),
  verifyCheck: (verification_token: string, code: string) =>
    api.post<GenericMessage>("/public/phone-verification/check", { verification_token, code }),
};

export const meApi = {
  profile: () => api.get<EmployeePublic>("/me/profile"),
  verifyStart: () => api.post<VerifySent>("/me/phone-verification/start"),
  verifyCheck: (code: string) => api.post<GenericMessage>("/me/phone-verification/check", { code }),
  assignments: (scope: "upcoming" | "past") => api.get<MyAssignment[]>(`/me/assignments${qs({ scope })}`),
};

export const shiftApi = {
  list: ({ needs_attention, ...f }: ShiftQuery) =>
    api.get<ShiftAdmin[]>(`/admin/shifts${qs({ ...f, needs_attention: needs_attention ? "true" : undefined })}`),
  get: (id: string) => api.get<ShiftAdmin>(`/admin/shifts/${id}`),
  create: (body: ShiftCreate) => api.post<BulkCreateResponse>("/admin/shifts", body),
  update: (id: string, body: ShiftInput) => api.put<ShiftAdmin>(`/admin/shifts/${id}`, body),
  confirm: (id: string) => api.post<ShiftAdmin>(`/admin/shifts/${id}/confirm`),
  setResponse: (id: string, response: CrewResponse | null) => api.put<ShiftAdmin>(`/admin/shifts/${id}/response`, { employee_response: response }),
  cancel: (id: string, reason: string) => api.post<ShiftAdmin>(`/admin/shifts/${id}/cancel`, { reason }),
  acknowledge: (id: string, flagId: string, note: string) =>
    api.post<ShiftAdmin>(`/admin/shifts/${id}/flags/${flagId}/acknowledge`, { note }),
  history: (id: string) => api.get<HistoryEntry[]>(`/admin/shifts/${id}/history`),
  employeeOptions: () => api.get<EmployeeOption[]>("/admin/shifts/employee-options"),
  venueImpact: (venueId: string) => api.get<{ future_assignments: number }>(`/admin/venues/${venueId}/impact`),
};

export const smsApi = {
  settings: () => api.get<{ key: string; value: unknown; description: string | null }[]>("/admin/settings"),
  setNumberSetting: (key: string, value: number) => api.put<{ key: string; value: unknown }>(`/admin/settings/${key}`, { value }),
  events: (f: Record<string, string | undefined>) => api.get<SmsEvent[]>(`/admin/sms/events${qs(f)}`),
  compliance: () => api.get<CompliancePanel>("/admin/sms/compliance"),
  templates: () => api.get<{ templates: TemplateInfo[]; placeholders: string[]; help_url_fallback: string }>("/admin/templates"),
  saveTemplate: (key: string, body: string) => api.put<TemplateInfo>(`/admin/templates/${key}`, { body }),
  resetTemplate: (key: string) => api.post<TemplateInfo>(`/admin/templates/${key}/reset`),
  previewTemplate: (key: string, body: string) => api.post<SegmentInfo & { preview: string }>(`/admin/templates/${key}/preview`, { body }),
  setTextSetting: (key: string, value: string) => api.put<{ key: string; value: string }>(`/admin/settings/text/${key}`, { value }),
  conflicts: (status?: string) => api.get<PhoneConflict[]>(`/admin/phone-conflicts${qs({ status })}`),
  resolveConflict: (id: string, action: "KEEP" | "RELEASE", note: string) =>
    api.post<PhoneConflict>(`/admin/phone-conflicts/${id}/resolve`, { action, note }),
  resendVerification: (employeeId: string) => api.post<VerifySent>(`/admin/employees/${employeeId}/verification/resend`),
  attempts: (employeeId: string) => api.get<VerificationAttempt[]>(`/admin/employees/${employeeId}/verification-attempts`),
  outbox: () => api.get<OutboxItem[]>("/admin/dev/mock-sms-outbox"),
  clearOutbox: () => api.post<GenericMessage>("/admin/dev/clear-mock-outbox"),
  simulate: (from_number: string, body: string) =>
    api.post<{ status_code: number; twiml: string; message_sid: string; from_masked: string }>("/admin/dev/simulate-inbound", { from_number, body }),
};
